// 레이드 하드(C13) 「잿불 낙인」 VFX — raidVfx.ts가 소유한 풀(가산·알파·빛 바닥·그을음·돌 조각·표식)을 함께 쓴다(드로우콜 추가 0).
// 게임 경고 칸(낙인 원 텔레그래프 — 위치·남은 틱)을 매 프레임 따라가고, 폭발은 사건(Ev.BossPattern a=brand, c=-1, d=주인 칸)으로 낸다.
// 시작 사건의 c(솔로 = 전체 마스크, 4인 넷 = 원마다 주인 비트)에는 기대지 않는다 — 원이 생길 때마다 덧붙이고 기존 표식은 지우지 않는다.
//   낙인 붙음: 주인 머리 위에 천천히 도는 잿불 룬(AddKind.Rune 표식 + 작은 심 광휘) + 발밑을 따라가는 조준 원(GlowKind.Target, 남은 시간만큼 차오름)
//              + 원 가장자리에서 맥동하는 주황→적색 고리(GlowKind.Shock 짧은 판). 맥동 간격은 남은 시간에 따라 0.9초 → 0.16초로 빨라진다.
//              룬에서 불씨가 한두 개씩 떨어진다.
//   폭발: 짧은 기둥형 화염(세운 빛기둥 둘 + 솟는 불꽃 덩이) + 바닥 충격파 고리 + 불똥 파편·돌 조각 + 연기·그을음 + 약한 카메라 펀치.
// 섬광 예산(AGENTS.md "1초 3회 이하"): 폭발 광휘(Flare)와 카메라 펀치는 0.1초 안에 함께 터진 낙인을 한 번으로 묶어 세고, 최근 1초에 3번이면 건너뛴다.
//   화면 전체 밝아짐 없음 — 광휘는 판정 원(반경 3.4m)보다 작게. 카메라는 기존 액션 카메라 펀치(3px, 110ms, 섬광 줄이기 35%)만, 지속 흔들림 없음.
// 입자 예산(품질 배율 1): 낙인 하나 유지 = 가산 불씨 ≈ 초당 6(수명 0.9초 → 동시 ~6) + 바닥 맥동 판 ≤ 3 + 조준 원 1 + 표식 2.
//   폭발 하나 = 가산 37(빛기둥 2·광휘 1·불꽃 14·불똥 18·고리 2) + 알파 연기 6 + 돌 조각 10 + 빛 바닥 2 + 그을음 1. 낙인 둘이 같이 터지면 두 배.
//   프레임 루프에서는 typed array·숫자 필드만 바꾼다(새 객체 없음).
import { RAID_HARD, WyvernPattern } from '../shared/content/raid';
import { AddKind, AlphaKind, Orient, type RaidMarkers, type RaidParticles } from './raidVfxParticles';
import { DarkKind, GlowKind, type RaidGround } from './raidVfxGround';
import type { RaidDebris } from './raidVfxDebris';

/** 동시에 따라가는 낙인 원 최대 수(4인 넷 판 = 파티 4) */
export const BRAND_SLOTS = 4;
/** 표식 칸 수(원마다 룬 + 심 광휘) */
export const BRAND_MARKERS = BRAND_SLOTS * 2;
const BR = RAID_HARD.raid_wyvern?.brand;
/** 낙인 판정 반경·준비 틱(하드 정의가 없으면 기본값) */
export const BRAND_RADIUS = BR?.radius ?? 3.4;
export const BRAND_WINDUP = BR?.windup ?? 240;
/** 맥동 간격(초): 붙은 직후 → 터지기 직전 */
export const BRAND_PULSE = { slow: 0.9, fast: 0.16 } as const;
/** 섬광 예산: 1초 창 안 최대 횟수, 함께 터진 것으로 묶는 간격(초) */
export const BRAND_FLASH = { perSec: 3, merge: 0.1 } as const;
/** 폭발 카메라 펀치(px·ms) — 착지(8px)보다 훨씬 약하게 */
export const BRAND_PUNCH = { px: 3, ms: 110 } as const;

const EMBER = 0xff7a1a, EMBER_HOT = 0xffa83a, FIRE = 0xff5410, RED = 0xff2a10, SMOKE = 0x2e2522;
/** 맥동 판 기억 칸(낙인마다) */
const PULSE_KEEP = 3;

/** 낙인 VFX가 쓰는 공유 풀 */
export interface BrandPools {
  readonly add: RaidParticles; readonly alpha: RaidParticles; readonly glow: RaidGround; readonly dark: RaidGround;
  readonly debris: RaidDebris; readonly marks: RaidMarkers;
}

export class RaidBrandVfx {
  /** QA 집계: 낙인이 붙은 횟수(원 기준)·폭발 수·맥동 수·건너뛴 섬광 수 */
  brands = 0; blasts = 0; pulses = 0; flashSkipped = 0;
  /**
   * 낙인 원 추적(최대 BRAND_SLOTS개 — 솔로 2, 4인 넷 판 4). 낙인 원은 게임 경고 칸(텔레그래프)으로 알아본다: 시작 사건의 c(솔로 = 전체 마스크,
   * 넷 = 원마다 따로 주인 비트)에 기대지 않고, 매 프레임 살아 있는 낙인 경고를 그대로 따라간다(덧붙이기 — 새 원이 와도 기존 표식은 그대로).
   * 칸별: 경고 칸 번호(-1 = 빈 칸), 이번 프레임에 봤나, 위치, 조준 원 판 번호, 맥동 위상, 맥동 판 번호(고리 버퍼), 불씨 누적
   */
  private readonly tele = new Int16Array(BRAND_SLOTS).fill(-1);
  private readonly seen = new Uint8Array(BRAND_SLOTS);
  private readonly px = new Float32Array(BRAND_SLOTS);
  private readonly pz = new Float32Array(BRAND_SLOTS);
  private readonly base = new Int32Array(BRAND_SLOTS).fill(-1);
  private readonly phase = new Float32Array(BRAND_SLOTS);
  private readonly pulseId = new Int32Array(BRAND_SLOTS * PULSE_KEEP).fill(-1);
  private readonly pulseAt = new Uint8Array(BRAND_SLOTS);
  private readonly acc = new Float32Array(BRAND_SLOTS);
  /** 섬광 시각 고리 버퍼(세계 초, -1e9 = 비었음) */
  private readonly flashT = new Float64Array(BRAND_FLASH.perSec).fill(-1e9);
  private flashAt = 0;
  private lastFlash = -1e9;

  constructor(private readonly P: BrandPools, private readonly markBase: number) {}

  /** 지금 따라가는 낙인 원 수 */
  get active(): number { let n = 0; for (let k = 0; k < BRAND_SLOTS; k++) if (this.tele[k] >= 0) n++; return n; }

  /** 프레임 시작(표식 begin 뒤): 이번 프레임에 본 원 표시를 지운다 */
  begin(): void { this.seen.fill(0); }

  /**
   * 살아 있는 낙인 경고 하나(경고 칸 tele, 게임 위치 x·z, 남은 틱 left / 전체 total). 처음 보는 칸이면 붙인다(덧붙이기).
   * 가까운 파티원(1.5m 안)이 있으면 그 보간 위치에 붙여 부드럽게 따라간다(경고 좌표는 게임 틱마다만 바뀐다). dt = 세계 초(멈추면 0)
   */
  track(tele: number, x: number, z: number, left: number, total: number, slotX: ArrayLike<number>, slotZ: ArrayLike<number>, dt: number, time: number): void {
    let k = -1, free = -1;
    for (let i = 0; i < BRAND_SLOTS; i++) { if (this.tele[i] === tele) { k = i; break; } if (free < 0 && this.tele[i] < 0) free = i; }
    let best = 1.5 * 1.5;
    for (let s = 0; s < slotX.length; s++) { const d = (slotX[s] - x) ** 2 + (slotZ[s] - z) ** 2; if (d < best) { best = d; x = slotX[s]; z = slotZ[s]; } }
    if (k < 0) { if (free < 0) return; k = free; this.attach(k, tele, x, z, left); }
    this.seen[k] = 1; this.px[k] = x; this.pz[k] = z;
    const prog = Math.min(1, Math.max(0, 1 - left / Math.max(1, total)));
    // 따라가는 조준 원(판정 원과 같은 반경). 없어졌으면(덮어쓰기) 남은 시간으로 다시 만든다
    if (this.base[k] < 0 || !this.P.glow.move(this.base[k], x, z)) this.base[k] = this.P.glow.add(GlowKind.Target, x, z, 0, BRAND_RADIUS, BRAND_RADIUS, BRAND_RADIUS, BRAND_RADIUS, Math.max(0.1, left / 60 + 0.05), 0xff4a1a, 1.5, 0.9);
    for (let j = 0; j < PULSE_KEEP; j++) { const id = this.pulseId[k * PULSE_KEEP + j]; if (id >= 0 && !this.P.glow.move(id, x, z)) this.pulseId[k * PULSE_KEEP + j] = -1; }
    this.runes(k, x, z, prog, time);
    if (dt > 0) { this.pulse(k, x, z, prog, dt); this.embers(k, x, z, prog, dt); }
  }

  /** 프레임 끝: 이번 프레임에 안 보인 원(판정·취소로 경고가 사라짐)은 표식을 거둔다 */
  end(): void { for (let k = 0; k < BRAND_SLOTS; k++) if (this.tele[k] >= 0 && !this.seen[k]) this.drop(k, 0.08); }

  private attach(k: number, tele: number, x: number, z: number, left: number): void {
    this.tele[k] = tele; this.phase[k] = 0.75; this.acc[k] = 0; this.brands++;
    this.base[k] = this.P.glow.add(GlowKind.Target, x, z, 0, BRAND_RADIUS, BRAND_RADIUS, BRAND_RADIUS, BRAND_RADIUS, Math.max(0.1, (left || BRAND_WINDUP) / 60 + 0.05), 0xff4a1a, 1.5, 0.9);
    // 붙는 순간: 머리 위에서 아래로 내려꽂히는 불씨 몇 개(표식이 '박혔다'는 신호)
    const A = this.P.add;
    for (let c = 0; c < A.count(8, 3); c++) {
      const a = A.range(0, Math.PI * 2);
      A.emit(AddKind.Ember, Orient.Camera, x + Math.sin(a) * 0.5, 3.2, z + Math.cos(a) * 0.5, -Math.sin(a) * 0.6, A.range(-5, -2.5), -Math.cos(a) * 0.6, 0.45, 0.2, 0.06, EMBER_HOT, 2.0, 0.95, -4, 0.5);
    }
  }

  private drop(k: number, sec: number): void {
    this.tele[k] = -1;
    if (this.base[k] >= 0) { this.P.glow.end(this.base[k], sec); this.base[k] = -1; }
    for (let j = 0; j < PULSE_KEEP; j++) { const i = k * PULSE_KEEP + j; if (this.pulseId[i] >= 0) { this.P.glow.end(this.pulseId[i], sec); this.pulseId[i] = -1; } }
  }

  /** 머리 위 룬: 천천히 돈다(끝으로 갈수록 빨리), 색은 주황 → 적색, 위아래로 살짝 뜬다 */
  private runes(s: number, x: number, z: number, prog: number, time: number): void {
    const M = this.P.marks, i = this.markBase + s * 2;
    const spin = (time * (0.35 + 0.9 * prog) + s * 0.27) % 1;
    const bob = Math.sin(time * 2.4 + s) * 0.08;
    const col = lerpColor(EMBER, RED, prog);
    const beat = 0.85 + 0.15 * Math.sin(time * (6 + 14 * prog));
    M.set(i, AddKind.Rune, Orient.Camera, x, 2.95 + bob, z, 1.15, 1, col, 1.9, 0.95 * beat, spin, 0.3 + s * 0.17);
    M.set(i + 1, AddKind.Flare, Orient.Camera, x, 2.95 + bob, z, 0.7, 1, EMBER_HOT, 1.2, 0.45 * beat);
  }

  /** 발밑 원 가장자리 맥동: 위상이 1을 넘을 때마다 얇은 고리 하나(바깥으로 조금 번지며 사라짐) */
  private pulse(s: number, x: number, z: number, prog: number, dt: number): void {
    const period = BRAND_PULSE.slow + (BRAND_PULSE.fast - BRAND_PULSE.slow) * Math.pow(prog, 1.4);
    this.phase[s] += dt / period;
    if (this.phase[s] < 1) return;
    this.phase[s] -= Math.floor(this.phase[s]);
    const k = s * PULSE_KEEP + this.pulseAt[s];
    this.pulseAt[s] = (this.pulseAt[s] + 1) % PULSE_KEEP;
    if (this.pulseId[k] >= 0) this.P.glow.end(this.pulseId[k], 0.02);
    const life = Math.min(0.4, period * 0.9);
    this.pulseId[k] = this.P.glow.add(GlowKind.Shock, x, z, 0, BRAND_RADIUS * 0.9, BRAND_RADIUS * 1.08, BRAND_RADIUS * 0.9, BRAND_RADIUS * 1.08, life, lerpColor(0xff8a2a, RED, prog), 1.4 + 0.5 * prog, 0.75);
    this.pulses++;
  }

  /** 룬에서 떨어지는 불씨(초당 4 → 9) */
  private embers(s: number, x: number, z: number, prog: number, dt: number): void {
    const A = this.P.add;
    this.acc[s] += dt * (4 + 5 * prog) * A.scale;
    while (this.acc[s] >= 1) {
      this.acc[s] -= 1;
      const a = A.range(0, Math.PI * 2), rr = A.range(0.1, 0.45);
      A.emit(AddKind.Ember, Orient.Camera, x + Math.sin(a) * rr, 2.75, z + Math.cos(a) * rr, A.range(-0.3, 0.3), A.range(-1.6, -0.6), A.range(-0.3, 0.3), A.range(0.7, 1.0), 0.14, 0.04, prog > 0.6 ? FIRE : EMBER, 1.9, 0.9, -1.5, 0.3);
    }
  }

  /**
   * 폭발(Ev.BossPattern a=brand, c=-1, x/z = 폭발 자리). 섬광 예산이 남으면 광휘·펀치를 켠다(펀치는 돌려준 값으로 호출자가 낸다).
   * 반환: 카메라 펀치를 낼지
   */
  explode(x: number, z: number, time: number): boolean {
    const A = this.P.add, S = this.P.alpha, R = BRAND_RADIUS;
    // 폭발 자리에 가장 가까운 원의 표식을 바로 거둔다(경고 칸은 같은 틱에 비므로 end()도 거두지만, 표식이 한 프레임 남지 않게)
    let near = -1, nd = 2.5 * 2.5;
    for (let k = 0; k < BRAND_SLOTS; k++) if (this.tele[k] >= 0) { const d = (this.px[k] - x) ** 2 + (this.pz[k] - z) ** 2; if (d < nd) { nd = d; near = k; } }
    if (near >= 0) this.drop(near, 0.05);
    this.blasts++;
    const flash = this.takeFlash(time);
    // 기둥형 화염: 세운 빛기둥 둘(바깥 주황 넓게, 안 노랑 좁게 — 높이 ≈ 6m, 0.45초)
    A.emit(AddKind.Pillar, Orient.Upright, x, 3.0, z, 0, 1.5, 0, 0.45, 2.2, 2.6, FIRE, 1.7, 0.9, 0, 0, 0, 2.7);
    A.emit(AddKind.Pillar, Orient.Upright, x, 2.6, z, 0, 2.2, 0, 0.32, 1.0, 1.3, EMBER_HOT, 1.9, 0.85, 0, 0, 0, 4.6);
    // 광휘(섬광 예산 안에서만) — 판정 원보다 작게
    if (flash) A.emit(AddKind.Flare, Orient.Camera, x, 1.2, z, 0, 0, 0, 0.2, 1.0, R * 0.9, EMBER_HOT, 1.3, 0.75);
    // 솟는 불꽃 덩이(기둥을 채운다)
    for (let c = 0; c < A.count(14, 5); c++) {
      const a = A.range(0, Math.PI * 2), rr = A.range(0, 0.7);
      A.emit(AddKind.Flame, Orient.Camera, x + Math.sin(a) * rr, A.range(0.3, 1.2), z + Math.cos(a) * rr, Math.sin(a) * A.range(0.3, 1.2), A.range(6, 12), Math.cos(a) * A.range(0.3, 1.2), A.range(0.4, 0.75), A.range(0.8, 1.2), A.range(1.6, 2.4), c % 3 ? FIRE : EMBER_HOT, 1.6, 0.8, -6, 2.5);
    }
    // 불똥 파편(바깥 + 위로)
    for (let c = 0; c < A.count(18, 6); c++) {
      const a = A.range(0, Math.PI * 2), s = A.range(6, 13);
      A.emit(AddKind.Streak, Orient.Velocity, x, 0.6, z, Math.sin(a) * s, A.range(3, 10), Math.cos(a) * s, A.range(0.35, 0.65), 0.08, 0.03, c % 2 ? EMBER_HOT : 0xfff0d0, 2.3, 1, -15, 1, 0.05);
    }
    // 낮게 퍼지는 고리 둘(공중 테두리)
    A.emit(AddKind.Ring, Orient.Camera, x, 1.0, z, 0, 0, 0, 0.3, 0.8, R * 0.85, 0xff9a40, 1.3, 0.5);
    A.emit(AddKind.Ring, Orient.Camera, x, 2.4, z, 0, 0.8, 0, 0.4, 0.6, R * 0.6, RED, 1.2, 0.4);
    // 바닥 충격파: 판정 원 가장자리까지 쓸고 나가는 고리 + 순간 열 원판
    this.P.glow.add(GlowKind.Shock, x, z, 0, 0.6, R * 1.35, 0.6, R * 1.35, 0.45, 0xff6a24, 1.6, 0.9);
    this.P.glow.add(GlowKind.Disc, x, z, 0, R * 0.8, R * 0.95, R * 0.8, R * 0.95, 0.35, FIRE, 1.1, 0.55);
    this.P.dark.add(DarkKind.Scorch, x, z, A.range(0, 6.28), R * 0.75, R * 0.8, R * 0.75, R * 0.8, 6, 0x120a08, 1, 0.7);
    this.P.debris.burst(x, z, 0.8, 10, 6, 8, 0.35, 0.9);
    for (let c = 0; c < S.count(6, 2); c++) {
      S.emit(AlphaKind.Smoke, Orient.Camera, x + S.range(-1, 1), 2.4, z + S.range(-1, 1), S.range(-0.6, 0.6), S.range(1.8, 3.2), S.range(-0.6, 0.6), S.range(1.3, 2.0), 1.4, 3.6, SMOKE, 1, 0.5, 0.2, 0.7);
    }
    return flash;
  }

  /** 섬광 예산: 0.1초 안에 함께 터진 것은 한 번으로 묶고, 최근 1초에 이미 3번이면 false */
  private takeFlash(time: number): boolean {
    if (time - this.lastFlash < BRAND_FLASH.merge) return true;
    const oldest = this.flashT[this.flashAt];
    if (time - oldest < 1) { this.flashSkipped++; return false; }
    this.flashT[this.flashAt] = time; this.flashAt = (this.flashAt + 1) % BRAND_FLASH.perSec;
    this.lastFlash = time;
    return true;
  }

  /** 이 사건이 낙인 폭발인가 */
  static isBlast(a: number, c: number): boolean { return a === WyvernPattern.brand && c < 0; }

  clear(): void {
    this.tele.fill(-1); this.seen.fill(0); this.base.fill(-1); this.pulseId.fill(-1); this.pulseAt.fill(0); this.phase.fill(0); this.acc.fill(0);
    this.flashT.fill(-1e9); this.flashAt = 0; this.lastFlash = -1e9;
  }
}

function lerpColor(a: number, b: number, t: number): number {
  const k = Math.max(0, Math.min(1, t));
  const r = Math.round((a >> 16 & 255) + ((b >> 16 & 255) - (a >> 16 & 255)) * k);
  const g = Math.round((a >> 8 & 255) + ((b >> 8 & 255) - (a >> 8 & 255)) * k);
  const bl = Math.round((a & 255) + ((b & 255) - (a & 255)) * k);
  return (r << 16) | (g << 8) | bl;
}
